import React, { useCallback, useEffect, useState } from 'react';

import { Group, Text, useMantineTheme } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconCheck, IconCircleCheckFilled, IconCircleOff, IconEdit, IconTrashX } from '@tabler/icons-react';
import Menu from 'components/Menu';
import Table from 'components/Table';
import { useLocalization } from 'context/localization-context';
import useFlows from 'hooks/flows/useFlows';
import useUpdateFlow from 'hooks/flows/useUpdateFlow';
import { useNavigate } from 'react-router-dom';
import { CellProps } from 'react-table';
import { Flow } from 'types';
import notificationHandler from 'utils/notifications';

import { formatFlowDeliveryModeText } from './FlowDeliveryModeFormatter';
import FlowNameCell from './FlowNameCell';
import ModalFlowDelete from './ModalFlowDelete';

interface IFlowsTableContainerProps {
  permission?: boolean;
  tableData: Flow[];
  setTableData: React.Dispatch<React.SetStateAction<Flow[]>>;
}

const FlowsTableContainer = ({
  tableData,
  setTableData,
  permission = true,
}: IFlowsTableContainerProps): JSX.Element => {
  const theme = useMantineTheme();
  const { timeFormat } = useLocalization();

  const [ordering, setOrdering] = useState('trigger');
  const [ascending, setAscending] = useState(true);
  const navigate = useNavigate();

  const { data, fetchNextPage, hasNextPage, isSuccess, isError, isLoading, isFetchingNextPage } = useFlows({
    ordering: ascending ? ordering : `-${ordering}`,
  });

  const [selected, setSelected] = useState<Flow | undefined>();

  const [deleteModalOpened, { open: openDeleteModal, close: closeDeleteModal }] = useDisclosure(false);

  const handleHeaderClick = useCallback(
    (header: string): void => {
      setOrdering(header);
      setAscending(!ascending);
    },
    [ascending],
  );
  const { successNotification, errorNotification } = notificationHandler();

  const handleEdit = (flow: Flow): void => {
    navigate(`/flow-builder/${flow.flowId}`);
  };

  const handleDelete = (flow: Flow): void => {
    setSelected(flow);
    openDeleteModal();
  };

  const updateMutation = useUpdateFlow();

  const handleToggleFlow = (flow: Flow): void => {
    updateMutation.mutate(
      {
        ...flow,
        enabled: !flow.enabled,
        notificationMessageTemplate: flow.notificationMessageTemplate.notificationMessageTemplateId,
      },
      {
        onSuccess: () => {
          if (flow.enabled) {
            successNotification('Flow successfully disabled');
          } else {
            successNotification('Flow successfully enabled');
          }
        },
        onError: error => {
          errorNotification('Flow toggle has failed: ', error.response?.data.message);
        },
      },
    );
  };

  const columns = [
    {
      Header: 'Name',
      accessor: 'name' as const,
      Cell: ({ row }: CellProps<Flow>) => (
        <FlowNameCell
          key={row.original.flowId}
          flow={row.original}
          onOpenFlow={() => handleEdit(row.original)}
        />
      ),
    },
    {
      Header: 'Flow trigger',
      accessor: 'trigger' as const,
      Cell: ({ row }: CellProps<Flow>) => (
        <Text
          onClick={() => handleEdit(row.original)}
          weight='bold'
          underline
          sx={{ cursor: 'pointer', width: 'fit-content' }}
        >
          {row.original.trigger}
        </Text>
      ),
    },
    {
      Header: 'Recipients',
      accessor: 'recipients' as const,
    },
    {
      Header: 'Delivery Mode',
      accessor: 'deliveryMode' as const,
    },
    {
      Header: 'Notification Time',
      accessor: 'timeAmount' as const,
      Cell: ({ row }: CellProps<Flow>) => (
        <Text sx={{ cursor: 'pointer', width: 'fit-content' }}>
          {formatFlowDeliveryModeText({
            trigger: row.original.trigger,
            timeUnit: row.original.timeUnit,
            timeAmount: row.original.timeAmount,
            deliveryMode: row.original.deliveryMode,
            sendAtTime: row.original.sendAtTime,
            timeFormat,
          })}
        </Text>
      ),
    },
    {
      Header: 'Notification Template',
      accessor: 'notificationMessageTemplate.description' as const,
    },
    {
      Header: 'Notification Type',
      accessor: 'notificationType' as const,
    },
    {
      Header: 'Enabled',
      accessor: 'enabled' as const,
      Cell: ({ row }: CellProps<Flow>) =>
        row.original.enabled ? (
          <IconCircleCheckFilled size={theme.fontSizes.xl} style={{ color: theme.colors.kiloBlue[6] }} />
        ) : (
          <IconCircleOff size={theme.fontSizes.sm} />
        ),
    },
    {
      id: 'action',
      Cell: ({ row }: CellProps<Flow>) => (
        <Group position='right'>
          <Menu
            position='bottom-end'
            disabled={!permission}
            items={[
              {
                title: row.original.enabled ? 'Disable' : 'Enable',
                icon: row.original.enabled ? (
                  <IconCircleOff size={theme.fontSizes.sm} />
                ) : (
                  <IconCheck size={theme.fontSizes.sm} />
                ),
                onClick: () => handleToggleFlow(row.original),
              },
              {
                title: 'Edit',
                onClick: () => handleEdit(row.original),
                icon: <IconEdit size={theme.fontSizes.sm} />,
              },
              {
                title: 'Delete',
                onClick: () => handleDelete(row.original),
                icon: <IconTrashX size={theme.fontSizes.sm} />,
                color: 'red',
              },
            ]}
          />
        </Group>
      ),
      minWidth: '50px',
      maxWidth: '50px',
      sticky: true,
    },
  ];

  useEffect(() => {
    if (!isLoading && data) {
      setTableData(data.pages.map(page => page.data.results).flat());
    }
  }, [isLoading, data, setTableData]);

  return (
    <>
      <Table
        tableData={tableData}
        columns={columns}
        isSuccess={isSuccess}
        isLoading={isLoading}
        isError={isError}
        isFetchingNextPage={isFetchingNextPage}
        hasNextPage={!!hasNextPage}
        fetchNextPage={fetchNextPage}
        clickableHeaders={['trigger', 'recipients', 'deliveryMode', 'notificationType', 'enabled']}
        handleHeaderClick={handleHeaderClick}
        ordering={ordering}
        ascending={ascending}
      />

      {selected && (
        <ModalFlowDelete
          opened={!!selected && deleteModalOpened}
          handleClose={closeDeleteModal}
          flow={selected}
        />
      )}
    </>
  );
};

export default FlowsTableContainer;
